import { useEffect, useRef } from "react";
import { driver } from "driver.js";
import type { Driver, DriveStep } from "driver.js";
import type { Screen } from "../nav/BottomNav";
import {
  TOUR_REPLAY_EVENT,
  markTourSeen,
  resetTourSeen,
  shouldStartTour,
} from "./tour-storage";

export { requestAppTourReplay } from "./tour-storage";

const AUTO_START_DELAY_MS = 700;
// Lets the chat composer portal mount after the tour navigates to the chat
// screen before the step is considered missing and skipped.
const STEP_WAIT_FOR_ELEMENT_MS = 2000;

interface AppTourProps {
  readonly enabled: boolean;
  readonly currentScreen: Screen;
  readonly onNavigate: (screen: Screen) => void;
}

export function AppTour({ enabled, currentScreen, onNavigate }: AppTourProps) {
  const activeTourRef = useRef<Driver | null>(null);
  const onNavigateRef = useRef(onNavigate);
  const currentScreenRef = useRef(currentScreen);

  useEffect(() => {
    onNavigateRef.current = onNavigate;
    currentScreenRef.current = currentScreen;
  });

  useEffect(() => {
    const startTour = () => {
      if (activeTourRef.current?.isActive()) return;
      const originalScreen = currentScreenRef.current;
      const navigate = onNavigateRef.current;
      console.log("[Frontend][AppTour] Starting product tour", { from: originalScreen });

      const steps: DriveStep[] = [
        {
          element: '[data-tour="chat"]',
          waitForElement: STEP_WAIT_FOR_ELEMENT_MS,
          onHighlightStarted: () => navigate("chat"),
          popover: {
            title: "Meet your AI coach",
            description: "Ask anything, log by text or voice.",
            side: "top",
            align: "center",
          },
        },
        {
          element: '[data-tour="quick-log"]',
          popover: {
            title: "Quick Log",
            description: "The fastest way to log — photo, voice, barcode, search and more.",
            side: "top",
            align: "center",
          },
        },
        {
          element: '[data-tour="nav-today"]',
          waitForElement: STEP_WAIT_FOR_ELEMENT_MS,
          onHighlightStarted: () => navigate("today"),
          popover: {
            title: "Today",
            description: "Your daily dashboard — calories, macros, meals and day status.",
            side: "top",
            align: "start",
          },
        },
        {
          element: '[data-tour="nav-plan"]',
          waitForElement: STEP_WAIT_FOR_ELEMENT_MS,
          onHighlightStarted: () => navigate("plan"),
          popover: {
            title: "Plan",
            description: "Your adaptive programme — targets, expenditure and weekly check-ins.",
            side: "top",
            align: "end",
          },
        },
        {
          element: '[data-tour="avatar"]',
          popover: {
            title: "Settings",
            description: "Tap your avatar for Settings — goals, preferences, reminders and demo mode.",
            side: "bottom",
            align: "end",
          },
        },
      ];

      const tour = driver({
        animate: true,
        showProgress: true,
        stagePadding: 6,
        stageRadius: 14,
        overlayColor: "#26332c",
        overlayOpacity: 0.55,
        popoverClass: "bitetrack-tour-popover",
        skipMissingElement: true,
        disableActiveInteraction: true,
        nextBtnText: "Next",
        prevBtnText: "Back",
        doneBtnText: "Done",
        steps,
        onDestroyed: () => {
          console.log("[Frontend][AppTour] Tour ended", { restoreScreen: originalScreen });
          markTourSeen(window.localStorage);
          activeTourRef.current = null;
          onNavigateRef.current(originalScreen);
        },
      });
      activeTourRef.current = tour;
      // Step 1 targets the chat composer, so land on the chat screen first.
      navigate("chat");
      tour.drive();
    };

    const handleReplay = () => {
      console.log("[Frontend][AppTour] Replay requested");
      resetTourSeen(window.localStorage);
      startTour();
    };
    window.addEventListener(TOUR_REPLAY_EVENT, handleReplay);

    let autoStartTimeout: number | undefined;
    if (enabled && shouldStartTour(window.localStorage)) {
      autoStartTimeout = window.setTimeout(() => {
        if (shouldStartTour(window.localStorage)) {
          console.log("[Frontend][AppTour] Auto-starting first-run product tour");
          startTour();
        }
      }, AUTO_START_DELAY_MS);
    }

    return () => {
      window.removeEventListener(TOUR_REPLAY_EVENT, handleReplay);
      if (autoStartTimeout !== undefined) window.clearTimeout(autoStartTimeout);
    };
  }, [enabled]);

  useEffect(() => {
    return () => {
      if (activeTourRef.current?.isActive()) {
        console.debug("[Frontend][AppTour] Destroying active tour on unmount");
        activeTourRef.current.destroy();
      }
    };
  }, []);

  return null;
}
